import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRecoilCallback, useRecoilValue } from 'recoil';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Paper, Tabs, Tab, Box, Link } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { gql, useMutation } from '@apollo/client';
import { accountIdSelectedAtom, accountsAtom, tokenAtom, urlLogoutAtom } from 'atoms';
import { Account } from 'types/Account';
import fragments from 'services/fragments';
import LoginAccountForm from './LoginAccountForm';
import LoginEmailForm from './LoginEmailForm';
import { checkPasswordStrength } from 'services/auth';
import { LoginVariables } from 'types/Login';
import GooglePlay from 'assets/googlePlay';
import AppStore from 'assets/appStore';
import axios from 'axios';

const LOGIN_EMAIL = gql`
  ${fragments.Accounts}
  mutation Login($email: String!, $password: String!, $app: String!) {
    login(email: $email, password: $password, app: $app) {
      token
      alreadyChangedPassword
      accounts {
        ...AccountProperties
      }
    }
  }
`;

interface LoginMutation {
  login: {
    token: string;
    alreadyChangedPassword: boolean;
    accounts: Account[];
  };
}

type LinksType = { android?: string; ios?: string };

function useAppLinks() {
  const accounts = useRecoilValue(accountsAtom);
  const accountIdSelected = useRecoilValue(accountIdSelectedAtom);

  const [links, setLinks] = useState<LinksType>();

  const { masterCompanyId, companyId } = useMemo(() => {
    if (!accounts) {
      return { masterCompanyId: 0, companyId: 0 };
    }

    return (
      accounts?.find((account) => account.id === accountIdSelected) ||
      accounts?.[0] || { masterCompanyId: 0, companyId: 0 }
    );
  }, [accounts, accountIdSelected]);

  useEffect(() => {
    async function fetchLinks() {
      try {
        const { data } = await axios.get<LinksType>(
          `${process.env.REACT_APP_API}/public/app-store/links?masterCompanyId=${
            masterCompanyId || 0
          }&companyId=${companyId || 0}`,
        );
        setLinks(data as LinksType);
      } catch (error) {}
    }
    fetchLinks();
  }, [masterCompanyId, companyId]);

  return { links };
}

function LoginPage() {
  const location = useLocation();
  // @ts-ignore
  const from = location.state?.from?.pathname || '/';
  const navigate = useNavigate();

  const { t } = useTranslation(['_common', 'login']);
  const [tab, setTab] = useState('email');
  const [password, setPassword] = useState('');
  const emailRef = useRef<string>('');

  const { links } = useAppLinks();

  const onCompleted = useRecoilCallback(
    ({ set }) =>
      (data: LoginMutation) => {
        if (data.login.accounts.length === 0) {
          throw new Error('user_without_enabled_accounts');
        }

        set(urlLogoutAtom, null);
        set(accountsAtom, data.login.accounts);

        const firstAccount = data.login.accounts[0];
        const checkResult = checkPasswordStrength(password);
        if (
          (!data.login.alreadyChangedPassword || checkResult?.hasFragility) &&
          firstAccount.email
        ) {
          navigate('/change-password', {
            state: {
              token: data.login.token,
              email: firstAccount.email,
              masterCompanyId: firstAccount.masterCompanyId,
              password,
            },
          });
        } else {
          set(tokenAtom, data.login.token);
          navigate(from);
        }
      },
    [from, password],
  );

  const [login, { loading: postingLogin, error: errorPostingLogin }] = useMutation<LoginMutation>(
    LOGIN_EMAIL,
    {
      variables: { app: 'web' },
      onCompleted,
    },
  );

  const handleLogin = useCallback(
    (variables: LoginVariables) => {
      setPassword(variables?.password || '');
      emailRef.current = variables.email;
      login({ variables });
    },
    [login],
  );

  if (
    errorPostingLogin?.message === 'user_is_already_logged_in_another_device' &&
    emailRef.current
  ) {
    return <Navigate to={`/already-logged?email=${emailRef.current}`} />;
  }

  return (
    <Box display="flex" flexDirection="column" gap={4}>
      <Paper square elevation={3}>
        <Tabs
          variant="fullWidth"
          value={tab}
          onChange={(e, newValue) => setTab(newValue)}
          aria-label={t('login:login_form')}
        >
          <Tab label={t('_common:account')} value="account" />
          <Tab label={t('_common:email')} value="email" />
        </Tabs>

        {tab === 'account' && (
          <LoginAccountForm {...{ handleLogin, postingLogin, errorPostingLogin }} />
        )}
        {tab === 'email' && (
          <LoginEmailForm {...{ handleLogin, postingLogin, errorPostingLogin }} />
        )}
      </Paper>

      {links && (
        <Box display="flex" justifyContent="center" gap={6}>
          {links?.android && (
            <Link href={links?.android} target="_blank">
              <GooglePlay />
            </Link>
          )}
          {links?.ios && (
            <Link href={links?.ios} target="_blank">
              <AppStore />
            </Link>
          )}
        </Box>
      )}
    </Box>
  );
}

export default LoginPage;
